import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import { vsprintf } from "sprintf-js";

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    marginTop: 35,
  },
  paper: {
    padding: theme.spacing(3),
  },
  formField: {
    margin: theme.spacing(1),
    width: "100%",
    maxWidth: 350,
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  summaryBox: {
    background: "#e3f2fd",
    border: "1px solid #90caf9",
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    textAlign: "center",
  },
  remainderHighlight: {
    background: "#fff3cd",
    border: "2px solid #ffc107",
    borderRadius: 8,
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    textAlign: "center",
  },
  errorMsg: {
    color: "#d32f2f",
    textAlign: "center",
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    fontSize: "13pt",
    fontWeight: 500,
  },
  "@media (min-width: 1024px)": {
    root: {
      width: 700,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 90,
    },
  },
});

const hebrewLabels = {
  pageTitle: "השלמת תשלום - הזמנת מקדמה",
  orderIdLabel: "מספר הזמנה",
  completionCodeLabel: "קוד השלמה",
  lookupButton: "חפש הזמנה",
  payRemainderButton: "שלם יתרה",
  lookupError: {
    ORDER_NOT_FOUND: "לא נמצאה הזמנה עם הפרטים שהוזנו.",
    INVALID_CODE: "קוד ההשלמה שגוי.",
    ALREADY_PAID: "הזמנה זו כבר שולמה במלאה.",
    INVALID_ORDER_STATUS: "סטטוס ההזמנה אינו מתאים להשלמת תשלום.",
    RATE_LIMITED: "יותר מדי ניסיונות. אנא נסו שוב מאוחר יותר.",
  },
  product: "מוצר",
  quantity: "כמות",
  price: "מחיר",
  depositPaid: "מקדמה ששולמה",
  remainingAmount: "יתרה לתשלום",
  fullTotal: "סה״כ מחיר מלא",
};

const reasonMessages = hebrewLabels.lookupError;

class RemainderPaymentPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      orderId: "",
      completionCode: "",
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const orderId = (params.get("orderId") || "").trim();
    const completionCode = (params.get("completionCode") || "")
      .trim()
      .toUpperCase()
      .slice(0, 6);

    if (!orderId && !completionCode) {
      return;
    }

    this.setState(
      {
        orderId,
        completionCode,
      },
      () => {
        if (orderId && completionCode.length === 6) {
          this.props.depositActions.lookupDepositOrder(
            orderId,
            completionCode
          );
        }
      }
    );
  }

  handleLookup = () => {
    const { orderId, completionCode } = this.state;
    this.props.depositActions.lookupDepositOrder(orderId, completionCode);
  };

  handlePayRemainder = () => {
    const { orderId, completionCode } = this.state;
    this.props.depositActions.initiateRemainderPayment(orderId, completionCode);
  };

  render() {
    const { classes, depositReducer } = this.props;
    const { lookupResult, lookupLoading, loading, error } = depositReducer;

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Typography
            variant="h5"
            style={{ textAlign: "center", marginBottom: 16 }}
          >
            {hebrewLabels.pageTitle}
          </Typography>

          {/* Step 1: Lookup form */}
          {!lookupResult || !lookupResult.ok ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <TextField
                className={classes.formField}
                label={hebrewLabels.orderIdLabel}
                value={this.state.orderId}
                onChange={(e) => this.setState({ orderId: e.target.value })}
                type="number"
                inputProps={{ min: 100000, max: 999999 }}
              />
              <TextField
                className={classes.formField}
                label={hebrewLabels.completionCodeLabel}
                value={this.state.completionCode}
                onChange={(e) =>
                  this.setState({
                    completionCode: e.target.value.toUpperCase(),
                  })
                }
                inputProps={{ maxLength: 6, style: { letterSpacing: 3, fontFamily: "'Courier New', monospace" } }}
              />

              {lookupResult && !lookupResult.ok && (
                <Typography className={classes.errorMsg}>
                  {reasonMessages[lookupResult.reason] ||
                    "אירעה שגיאה. אנא נסו שנית."}
                </Typography>
              )}

              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={this.handleLookup}
                  disabled={
                    lookupLoading ||
                    !this.state.orderId ||
                    this.state.completionCode.length < 6
                  }
                >
                  {lookupLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    hebrewLabels.lookupButton
                  )}
                </Button>
              </div>
            </div>
          ) : (
            /* Step 2: Show order summary and pay remainder */
            <div>
              <div className={classes.summaryBox}>
                <Typography variant="h6">
                  הזמנה מספר: {lookupResult.orderId}
                </Typography>
              </div>

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{hebrewLabels.product}</TableCell>
                    <TableCell>{hebrewLabels.quantity}</TableCell>
                    <TableCell>{hebrewLabels.price}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lookupResult.items.map((item) => (
                    <TableRow key={item.ean}>
                      <TableCell>
                        {item.brand} {item.displayName} {item.displaySize}
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {vsprintf("%.2f", [item.price * item.quantity])} ₪
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className={classes.remainderHighlight}>
                <Typography variant="body1">
                  {hebrewLabels.fullTotal}:{" "}
                  <strong>
                    {vsprintf("%.2f", [lookupResult.fullTotal])} ₪
                  </strong>
                </Typography>
                <Typography variant="body1">
                  {hebrewLabels.depositPaid}:{" "}
                  <strong>
                    {vsprintf("%.2f", [lookupResult.depositAmount])} ₪
                  </strong>
                </Typography>
                <Typography
                  variant="h6"
                  style={{ color: "#856404", marginTop: 8 }}
                >
                  {hebrewLabels.remainingAmount}:{" "}
                  <strong>
                    {vsprintf("%.2f", [lookupResult.remainingAmount])} ₪
                  </strong>
                </Typography>
              </div>

              {error && (
                <Typography className={classes.errorMsg}>
                  שגיאה: {error}
                </Typography>
              )}

              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={this.handlePayRemainder}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    `${hebrewLabels.payRemainderButton} - ${vsprintf("%.2f", [lookupResult.remainingAmount])} ₪`
                  )}
                </Button>
              </div>
            </div>
          )}
        </Paper>
      </div>
    );
  }
}

RemainderPaymentPage.propTypes = {
  classes: PropTypes.object.isRequired,
  depositReducer: PropTypes.object.isRequired,
  depositActions: PropTypes.object.isRequired,
};

export default withStyles(styles)(RemainderPaymentPage);
